import { useCallback, useEffect, useMemo, useState, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AssessmentIcon from "@mui/icons-material/Assessment";
import GroupsIcon from "@mui/icons-material/Groups";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Shell } from "../../common/shell/shell.page";
import { PageHeader } from "../../common/components/page-header";
import { LoadingOverlay } from "../../common/components/loading-overlay";
import application from "../../core/application";
import { Permission } from "../../users/user.entity";
import { SwalReact } from "../../common/swal-react";
import { Customer } from "../customer.entity";
import { customerRepository } from "../customer.repository";
import { customerAccountRepository } from "../customer-account.repository";
import { CustomerAccount } from "../customer-account.entity";
import { customerCreditService } from "../customer-credit.service";
import { formatBRL } from "../../sell/utils/sales-analytics";
import { useModuleAccess } from "../../settings/module-access";
import { ModuleLockedView } from "../../settings/components/module-locked-view";
import { v4 } from "uuid";

export const CustomersListPage = () => {
  application.useHasPermission(Permission.MANAGE_CUSTOMERS);
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [accounts, setAccounts] = useState<Map<string, CustomerAccount>>(new Map());
  const [search, setSearch] = useState("");
  const moduleAccess = useModuleAccess();

  const load = useCallback(async () => {
    setLoading(true);
    const list = await customerRepository.listByName(500);
    const accountMap = await customerAccountRepository.listByCustomerIds(
      list.map((c) => c.id)
    );
    setCustomers(list);
    setAccounts(accountMap);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return customers;
    return customers.filter(
      (c) =>
        c.getName().toLowerCase().includes(term) ||
        c.getPhone().toLowerCase().includes(term)
    );
  }, [customers, search]);

  const stats = useMemo(() => {
    const rows = customers.map((c) => ({
      customer: c,
      balance: accounts.get(c.id)?.getBalance() ?? 0,
    }));
    const debtors = rows.filter((r) => r.balance > 0);
    const totalDebt = debtors.reduce((sum, r) => sum + r.balance, 0);
    return {
      totalCustomers: customers.length,
      debtorsCount: debtors.length,
      totalDebt,
      averageDebt: debtors.length ? totalDebt / debtors.length : 0,
    };
  }, [accounts, customers]);

  const createCustomer = async () => {
    const customersLimit = moduleAccess.limits.customersLimit;
    if (Number.isFinite(customersLimit) && customers.length >= customersLimit) {
      await SwalReact.fire({
        title: "Limite de clientes atingido",
        text: "Seu plano atingiu o limite de clientes. Faça upgrade do módulo Clientes e Fiado para continuar.",
        icon: "warning",
      });
      return;
    }

    const result = await SwalReact.fire({
      title: "Novo cliente",
      html: (
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField id="customerName" label="Nome" fullWidth autoFocus />
          <TextField id="customerPhone" label="Telefone" fullWidth />
          <TextField id="customerNotes" label="Observações" fullWidth multiline minRows={3} />
        </Stack>
      ),
      showCancelButton: true,
      confirmButtonText: "Salvar",
      cancelButtonText: "Cancelar",
      preConfirm: () => {
        const name = (document.getElementById("customerName") as HTMLInputElement)?.value?.trim();
        const phone = (document.getElementById("customerPhone") as HTMLInputElement)?.value?.trim() || "";
        const notes = (document.getElementById("customerNotes") as HTMLInputElement)?.value?.trim() || "";
        if (!name) {
          SwalReact.showValidationMessage("Informe o nome do cliente.");
          return false;
        }
        return { name, phone, notes };
      },
    });

    if (!result.isConfirmed || !result.value) return;
    const customer = new Customer(v4(), result.value.name, result.value.phone, result.value.notes);
    await customerRepository.save(customer);
    await customerCreditService.ensureAccount(customer.id);
    await load();
  };

  if (loading || moduleAccess.loading) {
    return (
      <Shell title="Clientes">
        <LoadingOverlay message="Carregando clientes…" subtle />
      </Shell>
    );
  }

  if (!moduleAccess.activeCustomersTier) {
    return (
      <Shell title="Clientes">
        <PageHeader title="Clientes" description="Cadastro de clientes, fiado e pagamentos." />
        <ModuleLockedView
          moduleName="Clientes e Fiado"
          groupId="customers_credit"
          details="Ative este módulo para cadastrar clientes, vender fiado e controlar saldos devedores."
          features={[
            "Cadastro de clientes",
            "Venda fiado no PDV",
            "Saldo devedor e histórico",
            "Registro de pagamentos",
          ]}
        />
      </Shell>
    );
  }

  const customersLimit = moduleAccess.limits.customersLimit;
  const atLimit = Number.isFinite(customersLimit) && customers.length >= customersLimit;

  return (
    <Shell title="Clientes">
      <PageHeader
        title="Clientes"
        description="Cadastro, fiado e saldo de clientes em uma visão rápida."
        action={
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
            <Button
              variant="outlined"
              component={RouterLink}
              to="/clientes/relatorio"
              startIcon={<AssessmentIcon />}
            >
              Relatório de Fiado
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={createCustomer}
              disabled={atLimit}
              sx={{ fontWeight: 800 }}
            >
              Novo cliente
            </Button>
          </Stack>
        }
      />

      {/* Hero */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 2,
          border: 1,
          borderColor: "divider",
          borderRadius: 3,
          overflow: "hidden",
          position: "relative",
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.primary.dark, 0.02)} 100%)`,
        }}
      >
        <Box sx={{ position: "absolute", top: -20, right: -20, opacity: 0.04, pointerEvents: "none" }}>
          <GroupsIcon sx={{ fontSize: 180 }} />
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: "white",
              flexShrink: 0,
            }}
          >
            <GroupsIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box flex={1}>
            <Typography variant="h5" fontWeight={800} letterSpacing="-0.02em">
              Base de clientes
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gerencie cadastros, saldos de fiado e histórico de pagamentos.
            </Typography>
          </Box>
          <Chip
            icon={<GroupsIcon />}
            label={
              Number.isFinite(customersLimit)
                ? `${customers.length} de ${customersLimit} clientes`
                : `${customers.length} cliente${customers.length !== 1 ? "s" : ""}`
            }
            color={atLimit ? "warning" : "default"}
            variant={atLimit ? "filled" : "outlined"}
          />
        </Stack>
      </Paper>

      {atLimit && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Limite de clientes atingido. Faça upgrade do módulo para adicionar mais.
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <MiniStat label="Total de clientes" value={String(stats.totalCustomers)} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MiniStat
            label="Com dívida"
            value={String(stats.debtorsCount)}
            emphasize={stats.debtorsCount > 0 ? "warning" : undefined}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MiniStat
            label="Total em aberto"
            value={formatBRL(stats.totalDebt)}
            emphasize={stats.totalDebt > 0 ? "warning" : undefined}
            icon={<AccountBalanceWalletIcon fontSize="small" color="warning" />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MiniStat label="Ticket médio devido" value={formatBRL(stats.averageDebt)} />
        </Grid>
      </Grid>

      {/* Search */}
      <Paper
        elevation={0}
        sx={{ p: 1.5, mb: 2, border: 1, borderColor: "divider", borderRadius: 2 }}
      >
        <TextField
          size="small"
          placeholder="Buscar por nome ou telefone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <SearchIcon fontSize="small" sx={{ color: "text.disabled", mr: 1 }} />
            ),
          }}
        />
      </Paper>

      {/* Table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "action.hover" }}>
              <TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
              <TableCell sx={{ fontWeight: 700, display: { xs: "none", sm: "table-cell" } }}>
                Telefone
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Saldo</TableCell>
              <TableCell sx={{ fontWeight: 700, display: { xs: "none", md: "table-cell" } }}>
                Status
              </TableCell>
              <TableCell align="right" sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Box
                    sx={{ py: 6, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}
                  >
                    <PersonOutlineIcon sx={{ fontSize: 48, color: "text.disabled" }} />
                    <Typography color="text.secondary">
                      {search ? "Nenhum cliente encontrado para esta busca." : "Nenhum cliente cadastrado."}
                    </Typography>
                    {!search && (
                      <Button variant="outlined" startIcon={<AddIcon />} onClick={createCustomer} disabled={atLimit}>
                        Cadastrar primeiro cliente
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((customer) => {
                const balance = accounts.get(customer.id)?.getBalance() ?? 0;
                const hasDebt = balance > 0;
                return (
                  <TableRow
                    key={customer.id}
                    onClick={() => navigate(`/clientes/${customer.id}`)}
                    sx={{
                      "&:last-child td": { borderBottom: 0 },
                      "&:hover": { bgcolor: "action.hover" },
                      cursor: "pointer",
                      borderLeft: hasDebt ? `3px solid ${theme.palette.warning.main}` : "3px solid transparent",
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 2,
                            bgcolor: hasDebt ? "warning.main" : "primary.main",
                            color: "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 700,
                            fontSize: 16,
                            flexShrink: 0,
                          }}
                        >
                          {customer.getName()[0]?.toUpperCase() ?? "?"}
                        </Box>
                        <Typography fontWeight={700} noWrap>
                          {customer.getName()}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                      <Typography variant="body2" color="text.secondary">
                        {customer.getPhone() || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        fontWeight={700}
                        color={hasDebt ? "warning.dark" : "text.disabled"}
                        noWrap
                      >
                        {formatBRL(balance)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                      {hasDebt ? (
                        <Chip label="Com dívida" size="small" color="warning" variant="outlined" />
                      ) : (
                        <Chip label="Em dia" size="small" color="success" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Ver detalhes">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/clientes/${customer.id}`)}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Shell>
  );
};

function MiniStat({
  label,
  value,
  emphasize,
  icon,
}: {
  label: string;
  value: string;
  emphasize?: "error" | "warning";
  icon?: ReactNode;
}) {
  const color =
    emphasize === "error"
      ? "error.main"
      : emphasize === "warning"
      ? "warning.dark"
      : "text.primary";
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        height: "100%",
      }}
    >
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {icon}
        <Typography variant="h6" fontWeight={800} sx={{ color }}>
          {value}
        </Typography>
      </Stack>
    </Box>
  );
}
